import { useEffect, useRef } from "react";
import styled from "styled-components";

const TEXT_BASELINE_OFFSET = 140;

const CanvasWrap = styled.div`
  position: absolute;
  top: ${-TEXT_BASELINE_OFFSET + 12}px;
  left: 12px;
  right: 12px;
  bottom: 46px;
  pointer-events: none;
  z-index: 5;
  overflow: visible;
`;

const StyledCanvas = styled.canvas`
  display: block;
  width: 100%;
  height: 100%;
`;

const PROMPT_FONT = '400 14px "Figtree", system-ui, sans-serif';
const TEXT_COLOR = [255, 255, 255]; // #fff
const TEXT_ALPHA = 0.8;
const LINE_HEIGHT = 22;
const PARTICLE_STEP = 2;

const PromptDissolve = ({ text, onComplete }) => {
  const canvasRef = useRef(null);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!text) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.font = PROMPT_FONT;
    ctx.fillStyle = `rgba(${TEXT_COLOR[0]}, ${TEXT_COLOR[1]}, ${TEXT_COLOR[2]}, ${TEXT_ALPHA})`;
    ctx.textBaseline = "top";

    const words = text.split(/(\s+)/);
    const lines = [];
    let current = "";
    for (const word of words) {
      const probe = current + word;
      if (ctx.measureText(probe).width > width && current) {
        lines.push(current);
        current = word.trimStart();
      } else {
        current = probe;
      }
    }
    if (current) lines.push(current);

    lines.forEach((line, i) => {
      ctx.fillText(line, 0, TEXT_BASELINE_OFFSET + i * LINE_HEIGHT);
    });

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const step = PARTICLE_STEP * dpr;
    const particles = [];

    const LINE_STAGGER = 20;
    const SWEEP_FRAMES = 26;
    const lineHeightPx = LINE_HEIGHT * dpr;

    for (let py = 0; py < canvas.height; py += step) {
      for (let px = 0; px < canvas.width; px += step) {
        const idx = (py * canvas.width + px) * 4;
        const alpha = data[idx + 3];
        if (alpha > 15) {
          const lineIndex = Math.max(
            0,
            Math.floor((py - TEXT_BASELINE_OFFSET * dpr) / lineHeightPx),
          );
          const xRatio = px / canvas.width;
          const startFrame =
            lineIndex * LINE_STAGGER +
            xRatio * SWEEP_FRAMES +
            (Math.random() - 0.5) * 6;
          const startX = px / dpr;
          const startY = py / dpr;
          const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.6;
          const speed = 0.4 + Math.random() * 1.2;
          particles.push({
            originX: startX,
            originY: startY,
            x: startX,
            y: startY,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            life: 1,
            decay: 0.025 + Math.random() * 0.025,
            size: 1.4 + Math.random(),
            alphaBase: (alpha / 255) * TEXT_ALPHA,
            startFrame: Math.max(0, startFrame),
          });
        }
      }
    }

    if (!particles.length) {
      onCompleteRef.current?.();
      return undefined;
    }

    const maxStartFrame = particles.reduce(
      (m, p) => (p.startFrame > m ? p.startFrame : m),
      0,
    );

    ctx.clearRect(0, 0, width, height);

    let raf;
    let frame = 0;
    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      frame += 1;

      const allStarted = frame > maxStartFrame;

      if (!allStarted) {
        ctx.globalCompositeOperation = "source-over";
        ctx.fillStyle = `rgba(${TEXT_COLOR[0]}, ${TEXT_COLOR[1]}, ${TEXT_COLOR[2]}, ${TEXT_ALPHA})`;
        lines.forEach((line, i) => {
          ctx.fillText(line, 0, TEXT_BASELINE_OFFSET + i * LINE_HEIGHT);
        });

        ctx.globalCompositeOperation = "destination-out";
        ctx.fillStyle = "rgba(0, 0, 0, 1)";
        for (let i = 0; i < lines.length; i++) {
          const progress =
            (frame - i * LINE_STAGGER) / SWEEP_FRAMES;
          if (progress <= 0) continue;
          const sweepX = Math.min(1, progress) * width;
          const lineY = TEXT_BASELINE_OFFSET + i * LINE_HEIGHT - 3;
          ctx.fillRect(0, lineY, sweepX, LINE_HEIGHT + 6);
        }
      }

      ctx.globalCompositeOperation = "source-over";
      let alive = false;
      for (const p of particles) {
        if (frame < p.startFrame) {
          alive = true;
          continue;
        }
        if (p.life <= 0) continue;
        alive = true;

        p.x += p.vx;
        p.y += p.vy;
        p.vy -= 0.005;
        p.vx *= 0.975;
        p.life -= p.decay;

        const lifeClamped = Math.max(0, p.life);
        ctx.fillStyle = `rgba(${TEXT_COLOR[0]}, ${TEXT_COLOR[1]}, ${TEXT_COLOR[2]}, ${lifeClamped * p.alphaBase})`;
        ctx.fillRect(p.x, p.y, p.size, p.size);
      }

      if (alive) {
        raf = requestAnimationFrame(animate);
      } else {
        onCompleteRef.current?.();
      }
    };

    raf = requestAnimationFrame(animate);

    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [text]);

  if (!text) return null;

  return (
    <CanvasWrap aria-hidden="true">
      <StyledCanvas ref={canvasRef} />
    </CanvasWrap>
  );
};

export default PromptDissolve;
